import { useMemo, useEffect } from 'react';
import { classNames } from '@frontend/string';
import { theme } from '@frontend/theme';
import { NakedButton } from '../../naked/naked-button.component';
import { useFormField } from '../forms/hooks/use-form-field/use-form-field';
import { setValue } from '../forms/hooks/value-setters';
import { defaultConfig } from './const';
import { DayPickerOptions, DaysOfWeek } from './types';

type DayPickerConfig = {
  allowDisabledSelectedState?: boolean;
  options?: DayPickerOptions;
  onDayToggle?: ({ toggledDays, selectedDays }: { toggledDays: DaysOfWeek[]; selectedDays: DaysOfWeek[] }) => void;
};

export const DayPicker = ({
  config: _config = defaultConfig,
  selectedDays = [],
}: {
  config?: DayPickerConfig;
  selectedDays?: DaysOfWeek[];
}) => {
  const field = useFormField({ type: 'checklist', value: selectedDays });

  const config = useMemo(() => ({ ...defaultConfig, ..._config }), [_config]);

  /**
   * This effect is to handle the case where a day is disabled after being selected.
   */
  useEffect(() => {
    if (config.allowDisabledSelectedState) {
      return;
    }
    // If any selected day is now disabled, remove it from the selection
    const disabledDays = config.options.reduce(
      (acc, opt) => (opt.disabled ? acc.concat(opt.value) : acc),
      [] as DaysOfWeek[]
    );
    /**
     * The checklist field works in an XOR kind of way, so
     * if the value passed into the onChange exists, it will be removed.
     * if the value passed into the onChange does not exist, it will be added.
     * But if the value is an empty array, it will clear the entire selection.
     */
    const valuesToFilterOut = field.value.filter((v) => disabledDays.includes(v as DaysOfWeek)) as DaysOfWeek[];
    if (valuesToFilterOut.length) {
      field.onChange({ name: 'days', value: valuesToFilterOut });
      // This is to manually calculate the next state of the field so that we can pass it on to handlers
      const nextState = setValue({ ...field, type: 'checklist' }, valuesToFilterOut);
      config.onDayToggle({ toggledDays: valuesToFilterOut, selectedDays: nextState.value });
    }
  }, [config, config.options, field]);

  return (
    <div css={{ display: 'grid', gridTemplateColumns: 'repeat(7, max-content)', gap: theme.spacing(0.5) }}>
      {config.options.map(({ label, value, disabled }) => (
        <NakedButton
          css={{
            padding: theme.spacing(1),
            background: theme.colors.indigo10,
            border: `1px solid ${theme.colors.indigo10}`,
            borderRadius: '50%',
            color: theme.colors.indigo50,
            width: 32,
            height: 32,

            display: 'grid',
            placeContent: 'center',

            '&.disabled': {
              opacity: '50%',
              cursor: 'not-allowed',
            },

            '&.selected': {
              background: theme.colors.indigo50,
              color: theme.colors.indigo10,
              fontWeight: 'bold',
            },
          }}
          className={classNames({
            selected: field.value.includes(value),
            disabled: disabled,
          })}
          onClick={() => {
            field.onChange({ name: 'days', value: value });
            const nextState = setValue({ ...field, type: 'checklist' }, value);
            config.onDayToggle({ toggledDays: [value], selectedDays: nextState.value });
          }}
          key={value}
          value={value}
          disabled={disabled}
        >
          {label}
        </NakedButton>
      ))}
    </div>
  );
};
